import React from 'react';
import axios from '../axios';
import Loader from '../loader';
import ModalContentBaseXl from '../redux/components/modal/modal_content_base_xl';
import moment from 'moment';
import AboutPaymentModal from '../redux/components/event/about_payment_modal';

const MIN_PREPAID_PRICE = 500; // 事前決済の参加料金の下限値

const SectionPlans = ({
  eventId,
  reservationSystemFee,
  eventsPlans,
  setEventsPlans,
  isNew,
  isCompanyOwnedCircle,
  tunagateFeeRate,
}) => {
  const [isCreating, setIsCreating] = React.useState(false);
  const [editableEventsPlanIds, setEditableEventsPlanIds] = React.useState([]);

  const initialEventsPlan = {
    id: '',
    event_id: eventId,
    plan: '参加チケット',
    target_type: 2,
    capacity: 0,
    price: MIN_PREPAID_PRICE + (reservationSystemFee || 0),
    creator_price: MIN_PREPAID_PRICE,
    price_type: 3,
    role_type: 0,
    description: '',
    expired_at: null,
    is_application_allowed: true,
  };

  const addEventsPlan = React.useCallback(() => {
    setIsCreating(true);
    axios
      .post('/api/event_edit/create_events_plan', initialEventsPlan)
      .then((response) => {
        setEventsPlans((prev) => [...prev, response.data]);
        setEditableEventsPlanIds((prev) => [...prev, response.data.id]);
      })
      .finally(() => {
        setIsCreating(false);
      });
  });

  const removeEventsPlan = React.useCallback(
    (planId, index) => {
      axios
        .post('/api/event_edit/delete_events_plan', {
          event_id: eventId,
          id: planId,
        })
        .then(() => {
          setEventsPlans((prev) => {
            const newEventsPlans = [...prev];
            newEventsPlans.splice(index, 1);
            return newEventsPlans;
          });
        });
    },
    [eventId]
  );

  return (
    <section id="plans">
      <div className="container col-md-8 col-lg-6">
        <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-3 d-inline-block">
          参加チケット設定
        </p>
      </div>
      <div className={`container col-md-8 col-lg-6`}></div>
      {eventsPlans.map((eventsPlan, index) => (
        <EventsPlanBox
          key={eventsPlan.id}
          eventsPlan={eventsPlan}
          setEventsPlan={(plan) => {
            setEventsPlans((prev) => {
              const newEventsPlans = [...prev];
              newEventsPlans[index] = plan;
              return newEventsPlans;
            });
          }}
          removeEventsPlan={() => {
            removeEventsPlan(eventsPlan.id, index);
          }}
          isEditable={editableEventsPlanIds.includes(eventsPlan.id) || isNew}
          reservationSystemFee={reservationSystemFee}
          isRemovable={eventsPlans.length > 1 && eventsPlan.is_removable}
          isCompanyOwnedCircle={isCompanyOwnedCircle}
          tunagateFeeRate={tunagateFeeRate}
        />
      ))}
      {isCreating ? (
        <Loader isActive={isCreating} />
      ) : (
        <div className="container col-md-8 col-lg-6 p-0">
          <div className="d-flex justify-content-end mr-1">
            <button
              className="bg-white mt-1 border border-dark"
              onClick={() => addEventsPlan()}
            >
              <p className="text-fz-14px text-black-333 text-fw-normal m-1">
                ＋ チケットの追加
              </p>
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
const EventsPlanBox = ({
  eventsPlan,
  setEventsPlan,
  removeEventsPlan,
  isEditable,
  reservationSystemFee,
  isRemovable,
  isCompanyOwnedCircle,
  tunagateFeeRate,
}) => {
  const modalRef = React.useRef();

  const updateEventsPlan = React.useCallback(() => {
    axios.post('/api/event_edit/update_events_plan', eventsPlan);
  }, [eventsPlan]);

  React.useEffect(() => {
    // 事前決済 (price_type=3)で参加料金が下限値未満の場合は保存しない
    if (eventsPlan.price_type === 3 && Number(eventsPlan.creator_price) < MIN_PREPAID_PRICE) {
      return;
    }
    updateEventsPlan();
  }, [eventsPlan]);

  return (
    <div className="mb-2">
      <div className="shadow-sm mb-0 bg-white">
        <div className="container col-md-8 col-lg-6 p-3_5 border-bottom">
          <div className="row justify-content-between p-1">
            <p className="col-4 pr-2 m-0">チケット名</p>
            <input
              id="plan"
              className="col-8 p-0 w-100 textarea--eventreport text-right"
              value={eventsPlan.plan}
              onBlur={updateEventsPlan}
              onChange={(e) =>
                setEventsPlan({ ...eventsPlan, plan: e.target.value })
              }
              placeholder="参加チケット"
            />
          </div>
        </div>
      </div>
      <div className="shadow-sm mb-0 bg-white">
        {eventsPlan.price_type === 99 ? null : (
          <div className="container col-md-8 col-lg-6 border-bottom p-3_5">
            <div className="row justify-content-between p-1">
              <div className="col d-flex align-items-start">
                <p>
                  参加料金
                  {eventsPlan.price_type === 3 && `（${MIN_PREPAID_PRICE}円以上）`}
                </p>
              </div>
              <div className="col-5 d-flex justify-content-end align-items-start">
                <input
                  id="creator_price"
                  className="p-0 textarea--eventreport text-right"
                  type="text"
                  min={0}
                  value={eventsPlan.creator_price}
                  onBlur={(e) => {
                    setEventsPlan({
                      ...eventsPlan,
                      creator_price:
                        !Number.isNaN(Number(e.target.value)) &&
                        Number(e.target.value) >= MIN_PREPAID_PRICE
                          ? Number(e.target.value)
                          : MIN_PREPAID_PRICE,
                      price:
                        !Number.isNaN(Number(e.target.value)) &&
                        Number(e.target.value) >= MIN_PREPAID_PRICE
                          ? Number(e.target.value)
                          : MIN_PREPAID_PRICE,
                    });
                  }}
                  onChange={(e) =>
                    setEventsPlan({
                      ...eventsPlan,
                      creator_price:
                        !Number.isNaN(Number(e.target.value)) &&
                        Number(e.target.value) > 0
                          ? Number(e.target.value)
                          : 0,
                      price:
                        !Number.isNaN(Number(e.target.value)) &&
                        Number(e.target.value) > 0
                          ? Number(e.target.value)
                          : 0,
                    })
                  }
                  placeholder="必須"
                  style={{ width: '100px'}}
                  disabled={!isEditable}
                />
                <span className="p-0 text-right">円</span>
              </div>
            </div>
            {eventsPlan.price_type === 3 && (
              <div className="d-flex justify-content-end text-fz-small text-black-50">
                <div>クリエイターの手取り：</div>
                <div>
                  <div>
                    {Math.round(
                      (eventsPlan.creator_price) *
                        (1 - tunagateFeeRate)
                    )}
                    円（振込で引き出しの場合）
                  </div>
                  <div>
                    {eventsPlan.creator_price}
                    円（ポイント引き出しの場合）
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div className=" container col-md-8 col-lg-6 border-bottom p-3_5">
          <div className="row justify-content-between p-1">
            <p className="col-4 pr-2 m-0">決済方法</p>
            {isEditable /* selectタグだとiOSで画面が泳ぐので場合分け */ ? (
              <select
                id="price_type"
                onBlur={() => updateEventsPlan()}
                onChange={(e) => {
                  setEventsPlan({
                    ...eventsPlan,
                    price_type: Number(e.target.value),
                  });
                }}
                value={eventsPlan.price_type}
                className="col-8 p-0 textarea--eventreport text-gray text-right"
                style={{ overflow: 'hidden' }}
                disabled={true}
              >
                {/* <option value={99}>無料（TOPや検索一覧に表示されません）</option> */}
                  <option value={3}>事前決済（クレジット / Amazon Pay）</option>
                {/* <option value={1}>現地決済（TOPや検索一覧に表示されません）</option> */}
              </select>
            ) : (
              <span className="col-8 p-0 textarea--eventreport text-gray text-right">
                {eventsPlan.price_type == 3
                  ? '事前決済（クレジット / Amazon Pay）'
                  : eventsPlan.price_type == 1
                  ? '現地決済（TOPや検索一覧に表示されません）'
                  : '無料（TOPや検索一覧に表示されません）'}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="shadow-sm mb-3 bg-white">
        <div className="container col-md-8 col-lg-6 p-3_5">
          <div className="row d-flex justify-space-between">
            <div className="col-7">
              {isRemovable && (
                <button
                  className="bg-white mt-1 w-100 border border-dark"
                  onClick={() => removeEventsPlan()}
                >
                  <p className="text-fz-14px text-black-333 text-fw-normal m-1">
                    ー チケットの削除
                  </p>
                </button>
              )}
            </div>
            <div className="col-5">
              <ModalContentBaseXl
                ref={modalRef}
                previewContent={
                  <p className="p-0 text-primary text-right bg-white border-0 m-1">
                    詳細設定する
                  </p>
                }
                modalContent={
                  <PlanDetailsSettingModal
                    closeModal={() => modalRef.current.closeModal()}
                    eventsPlan={eventsPlan}
                    setEventsPlan={setEventsPlan}
                    isCompanyOwnedCircle={isCompanyOwnedCircle}
                    reservationSystemFee={reservationSystemFee}
                    isEditable={isEditable}
                    updateEventsPlan={updateEventsPlan}
                    tunagateFeeRate={tunagateFeeRate}
                  />
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PlanDetailsSettingModal = ({
  closeModal,
  eventsPlan,
  setEventsPlan,
  isCompanyOwnedCircle,
  reservationSystemFee,
  isEditable,
  updateEventsPlan,
  tunagateFeeRate,
}) => {
  const [isAgeRangeErrorOn, setIsAgeRangeErrorOn] = React.useState(false);
  const [
    isParticipationCountRangeErrorOn,
    setIsParticipationCountRangeErrorOn,
  ] = React.useState(false);

  React.useEffect(() => {
    if (
      eventsPlan.age_from &&
      eventsPlan.age_to &&
      eventsPlan.age_from > eventsPlan.age_to
    ) {
      setIsAgeRangeErrorOn(true);
    } else {
      setIsAgeRangeErrorOn(false);
    }
    if (
      eventsPlan.past_participation_count_from &&
      (eventsPlan.past_participation_count_to ||
        eventsPlan.past_participation_count_to === 0) &&
      eventsPlan.past_participation_count_from >
        eventsPlan.past_participation_count_to
    ) {
      setIsParticipationCountRangeErrorOn(true);
    } else {
      setIsParticipationCountRangeErrorOn(false);
    }
  }, [eventsPlan]);

  React.useEffect(() => {
    if (window.innerWidth >= 768) {
      $('.datetimepicker').each(function () {
        $(this).datetimepicker({
          format: 'Y/m/d H:i',
          lang: 'ja',
          scrollInput: true,
          timepicker: true,
          step: 5,
        });
      });
    }
  }, []);

  let datetimeFormType;
  let datetimeFormat;
  if (window.innerWidth >= 768) {
    datetimeFormType = 'text';
    datetimeFormat = 'YYYY/MM/DD HH:mm';
    $('.datetimepicker').each(function () {
      $(this).datetimepicker({
        format: 'Y/m/d H:i',
        lang: 'ja',
        scrollInput: true,
        timepicker: true,
        step: 5,
      });
    });
  } else {
    datetimeFormType = 'datetime-local';
    datetimeFormat = 'YYYY-MM-DDTHH:mm';
  }

  return (
    <div className="card bg-gray" style={{ paddingBottom: '144px' }}>
      <div
        className="bg-white w-100 border-bottom"
        style={{
          zIndex: 9998,
          borderRadius: '16px 16px 0 0',
          position: 'fixed',
        }}
      >
        <button
          type="button"
          className="close close__modal position--modalclose"
          aria-label="Close"
          onClick={() => closeModal()}
        >
          <span className="text-fz-24px float-right m-2" aria-hidden="true">
            ×
          </span>
        </button>
      </div>
      <div className="bg-white h-100">
        <main
          style={{ paddingBottom: '180px' }}
          className="bg-gray h-main100vh"
        >
          <div className="container col-md-8 col-lg-6 pt-5">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-0 d-inline-block">
              プラン名
            </p>
            <p className="text-fz-small text-black-50 mb-2">
              早期に申し込むとお得になる「早割」プランの活用は有効です。申込期限や人数で区切って早割りプランを作りましょう。
            </p>
          </div>
          <div className="shadow-sm mb-0 bg-white">
            <div className=" container col-md-8 col-lg-6 p-3_5">
              <input
                id="plan"
                className="p-0 w-100 textarea--eventreport"
                value={eventsPlan.plan}
                onBlur={() => updateEventsPlan()}
                onChange={(e) =>
                  setEventsPlan({ ...eventsPlan, plan: e.target.value })
                }
                placeholder="プラン名を入力(例：イベント+飲み会）"
              />
            </div>
          </div>
          {isCompanyOwnedCircle && (
            <React.Fragment>
              <div className="container col-md-8 col-lg-6">
                <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-3 mr-3 d-inline-block">
                  役割 ※つなげーと公式イベント限定設定
                </p>
              </div>
              <div className="shadow-sm mb-0 bg-white">
                <div className=" container col-md-8 col-lg-6 p-3_5">
                  <select
                    id="role_type"
                    onBlur={() => updateEventsPlan()}
                    onChange={(e) => {
                      setEventsPlan({
                        ...eventsPlan,
                        role_type: Number(e.target.value),
                      });
                    }}
                    value={eventsPlan.role_type}
                    className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                  >
                    <option value={0}>一般</option>
                    <option value={1}>ファシリテーター</option>
                    {/* <option value={2}>スタッフ</option> */}
                  </select>
                </div>
              </div>
            </React.Fragment>
          )}
          <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-3 d-inline-block">
              対象者
            </p>
          </div>
          <div className="shadow-sm mb-0 bg-white">
            <div className=" container col-md-8 col-lg-6 border-bottom p-3_5">
              <select
                id="target_type"
                onBlur={() => updateEventsPlan()}
                onChange={(e) => {
                  setEventsPlan({
                    ...eventsPlan,
                    target_type: Number(e.target.value),
                  });
                }}
                value={eventsPlan.target_type}
                className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                disabled={!isEditable}
              >
                <option value={2}>サークルメンバー+非メンバー</option>
                <option value={1}>サークルメンバー</option>
                <option value={0}>非メンバー</option>
              </select>
            </div>
            <div className=" container col-md-8 col-lg-6 p-3_5">
              <select
                id="capacity"
                placeholder="募集人数を選択"
                onBlur={() => updateEventsPlan()}
                onChange={(e) => {
                  setEventsPlan({
                    ...eventsPlan,
                    capacity: Number(e.target.value),
                  });
                }}
                value={eventsPlan.capacity}
                className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
              >
                <option value={0}>人数制限なし</option>
                {[...Array(100)].map((_, i) => {
                  return (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}人
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          {eventsPlan.price_type !== 99 && (
            <div className="container col-md-8 col-lg-6">
              <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-3 mr-3 d-inline-block">
                会費
                {eventsPlan.price_type === 3 && `（${MIN_PREPAID_PRICE}円以上）`}
              </p>
              {/* <p className="text-fz-small text-black-50 mb-2">
              現地決済を設定せず、すべてのチケットが事前決済（クレジット /
              Amazon Pay）で設定されているイベントを表示上で優遇します。
            </p> */}
            </div>
          )}
          <div className="shadow-sm mb-0 bg-white">
            {eventsPlan.price_type === 99 ? null : (
              <div className="container col-md-8 col-lg-6 border-bottom p-3_5 d-flex justify-content-between">
                <div>
                  <input
                    id="creator_price"
                    className="p-0 textarea--eventreport"
                    type="text"
                    min={0}
                    value={eventsPlan.creator_price}
                    onBlur={(e) => {
                      setEventsPlan({
                        ...eventsPlan,
                        creator_price:
                          !Number.isNaN(Number(e.target.value)) &&
                          Number(e.target.value) >= MIN_PREPAID_PRICE
                            ? Number(e.target.value)
                            : MIN_PREPAID_PRICE,
                        price:
                          !Number.isNaN(Number(e.target.value)) &&
                          Number(e.target.value) >= MIN_PREPAID_PRICE
                            ? Number(e.target.value)
                            : MIN_PREPAID_PRICE,
                      });
                    }}
                    onChange={(e) =>
                      setEventsPlan({
                        ...eventsPlan,
                        creator_price:
                          !Number.isNaN(Number(e.target.value)) &&
                          Number(e.target.value) > 0
                            ? Number(e.target.value)
                            : 0,
                        price:
                          !Number.isNaN(Number(e.target.value)) &&
                          Number(e.target.value) > 0
                            ? Number(e.target.value)
                            : 0,
                      })
                    }
                    placeholder="料金を入力(半角数字)"
                    style={{ width: '160px' }}
                    disabled={!isEditable}
                  />
                  円
                  {eventsPlan.price_type === 3 && (
                    <div className="text-fz-small text-black-50">
                      <div className="mt-1">
                        <div>クリエイターの手取り：</div>
                        <div>
                          <div>
                            {Math.round(
                              (eventsPlan.creator_price) *
                                (1 - tunagateFeeRate)
                            )}
                            円（振込で引き出しの場合）
                          </div>
                          <div>
                            {eventsPlan.creator_price}
                            円（ポイント引き出しの場合）
                          </div>
                        </div>
                      </div>
                      <div>
                        チケット金額は手数料を加算して表示される場合がございます
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {eventsPlan.price_type === 3 && (
              <div className=" container col-md-8 col-lg-6 p-3_5">
                <select
                  id="price_type"
                  onBlur={() => updateEventsPlan()}
                  onChange={(e) => {
                    setEventsPlan({
                      ...eventsPlan,
                      price_type: Number(e.target.value),
                    });
                  }}
                  value={eventsPlan.price_type}
                  className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                  disabled={true}
                >
                  {/* <option value={99}>無料（TOPや検索一覧に表示されません）</option> */}
                    <option value={3}>
                      事前決済（クレジット / Amazon Pay）
                    </option>
                  {/* <option value={1}>現地決済（TOPや検索一覧に表示されません）</option> */}
                </select>
              </div>
            )}
            {Number(eventsPlan.price_type) === 3 && (
              <div className="container col-md-8 col-lg-6 pb-3">
                <AboutPaymentModal />
              </div>
            )}
          </div>
          <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-3 d-inline-block">
              チケットの説明
            </p>
            <p className="text-fz-small text-black-50 mb-2">
              参加料金以外に現地で掛かる費用についてはここに記入してください。
            </p>
          </div>
          <div className="shadow-sm mb-0 bg-white">
            <div className=" container col-md-8 col-lg-6 p-3_5">
              <input
                id="description"
                className="p-0 w-100 textarea--eventreport"
                value={eventsPlan.description}
                onBlur={() => updateEventsPlan()}
                onChange={(e) => {
                  setEventsPlan({ ...eventsPlan, description: e.target.value });
                }}
                placeholder="説明が必要な場合は記入"
              />
            </div>
          </div>

          {/* <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2_5 mt-3 d-inline-block">
              初回参加者の割引設定
            </p>
            <p className="text-fz-small text-black-50 mb-2">
              このサークルのイベントに初めて参加する場合の割引率設定
            </p>
          </div>
          <div className="shadow-sm mb-3 bg-white">
            <div className=" container col-md-8 col-lg-6 ">
              <div className="row justify-content-between p-3_5 border-bottom">
                <select
                  id="first_participant_discount_rate"
                  onBlur={() => updateEventsPlan()}
                  onChange={(e) => {
                    const discount_rate = e.target.value;

                    if (discount_rate === '割引しない') {
                      setEventsPlan({
                        ...eventsPlan,
                        first_participant_discount_rate: 0,
                      });
                    } else {
                      setEventsPlan({
                        ...eventsPlan,
                        first_participant_discount_rate: Number(
                          discount_rate.slice(0, -1)
                        ),
                      });
                    }
                  }}
                  value={
                    !eventsPlan.first_participant_discount_rate ||
                    eventsPlan.first_participant_discount_rate === 0
                      ? '割引しない'
                      : `${eventsPlan.first_participant_discount_rate}%`
                  }
                  className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                >
                  {['割引しない', '100%', '50%', '30%', '20%', '10%'].map(
                    (i) => (
                      <option value={i} key={i}>
                        {i}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>
          </div> */}

          <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2_5 mt-3 d-inline-block">
              年齢制限
            </p>
            <p className="text-fz-small text-black-50 mb-2">
              設定範囲外のユーザーは申し込みできなくなります
            </p>
          </div>
          <div className="shadow-sm mb-3 bg-white">
            <div className=" container col-md-8 col-lg-6 ">
              <div className="row justify-content-between p-3_5 border-bottom">
                <div className="text-fz-small text-black-50">上限</div>
                <select
                  id="age_to"
                  onBlur={() => updateEventsPlan()}
                  onChange={(e) => {
                    const age = e.target.value;

                    if (age === '設定しない') {
                      setEventsPlan({
                        ...eventsPlan,
                        age_to: null,
                      });
                    } else {
                      setEventsPlan({
                        ...eventsPlan,
                        age_to: Number(age),
                      });
                    }
                  }}
                  value={eventsPlan.age_to || '設定しない'}
                  className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                >
                  <option value={'設定しない'} key={'設定しない'}>
                    設定しない
                  </option>
                  {Array.from({ length: 100 - 14 }, (_, idx) => idx + 14).map(
                    (age) => {
                      if (
                        eventsPlan.age_from === null ||
                        eventsPlan.age_from === undefined
                      ) {
                        return (
                          <option value={age} key={age}>
                            {age}歳
                          </option>
                        );
                      } else {
                        if (age >= eventsPlan.age_from) {
                          return (
                            <option value={age} key={age}>
                              {age}歳
                            </option>
                          );
                        }
                      }
                    }
                  )}
                </select>
                <div className="text-fz-small text-black-50">下限</div>
                <select
                  id="age_from"
                  onBlur={() => updateEventsPlan()}
                  onChange={(e) => {
                    const age = e.target.value;

                    if (age === '設定しない') {
                      setEventsPlan({
                        ...eventsPlan,
                        age_from: null,
                      });
                    } else {
                      setEventsPlan({
                        ...eventsPlan,
                        age_from: Number(age),
                      });
                    }
                  }}
                  value={eventsPlan.age_from || '設定しない'}
                  className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                >
                  <option value={'設定しない'} key={'設定しない'}>
                    設定しない
                  </option>
                  {Array.from({ length: 100 - 14 }, (_, idx) => idx + 14).map(
                    (age) => {
                      if (
                        eventsPlan.age_to === null ||
                        eventsPlan.age_to === undefined
                      ) {
                        return (
                          <option value={age} key={age}>
                            {age}歳
                          </option>
                        );
                      } else {
                        if (age <= eventsPlan.age_to) {
                          return (
                            <option value={age} key={age}>
                              {age}歳
                            </option>
                          );
                        }
                      }
                    }
                  )}
                </select>
              </div>
            </div>
          </div>
          {isAgeRangeErrorOn && (
            <div
              className="container col-md-8 col-lg-6 pt-2"
              style={{ fontSize: '12px' }}
            >
              <span style={{ color: '#ff5252' }}>下限が上限より小さいです</span>
            </div>
          )}

          <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2_5 mt-3 d-inline-block">
              サークルの過去イベント参加回数
            </p>
            <p className="text-fz-small text-black-50 mb-2">
              設定範囲外のユーザーは申し込みできなくなります
            </p>
          </div>
          <div className="shadow-sm mb-3 bg-white">
            <div className=" container col-md-8 col-lg-6 ">
              <div className="row justify-content-between p-3_5 border-bottom">
                <div className="text-fz-small text-black-50">上限</div>
                <select
                  id="past_participation_count_to"
                  onBlur={() => updateEventsPlan()}
                  onChange={(e) => {
                    const participationCount = e.target.value;

                    if (participationCount === '設定しない') {
                      setEventsPlan({
                        ...eventsPlan,
                        past_participation_count_to: null,
                      });
                    } else {
                      setEventsPlan({
                        ...eventsPlan,
                        past_participation_count_to: Number(participationCount),
                      });
                    }
                  }}
                  value={
                    eventsPlan.past_participation_count_to ||
                    (eventsPlan.past_participation_count_to === 0
                      ? 0
                      : '設定しない')
                  }
                  className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                >
                  <option value={'設定しない'} key={'設定しない'}>
                    設定しない
                  </option>
                  <option value={0} key={0}>
                    初参加のみ
                  </option>
                  <option value={1} key={1}>
                    過去1回以下
                  </option>
                  <option value={2} key={2}>
                    過去2回以下
                  </option>
                  <option value={3} key={3}>
                    過去3回以下
                  </option>
                  <option value={5} key={5}>
                    過去5回以下
                  </option>
                  <option value={10} key={10}>
                    過去10回以下
                  </option>
                </select>
                <div className="text-fz-small text-black-50">下限</div>
                <select
                  id="past_participation_count_from"
                  onBlur={() => updateEventsPlan()}
                  onChange={(e) => {
                    const participationCount = e.target.value;

                    if (participationCount === '設定しない') {
                      setEventsPlan({
                        ...eventsPlan,
                        past_participation_count_from: null,
                      });
                    } else {
                      setEventsPlan({
                        ...eventsPlan,
                        past_participation_count_from:
                          Number(participationCount),
                      });
                    }
                  }}
                  value={
                    eventsPlan.past_participation_count_from || '設定しない'
                  }
                  className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
                >
                  <option value={'設定しない'} key={'設定しない'}>
                    設定しない
                  </option>
                  <option value={1} key={1}>
                    過去1回以上
                  </option>
                  <option value={2} key={2}>
                    過去2回以上
                  </option>
                  <option value={3} key={3}>
                    過去3回以上
                  </option>
                  <option value={5} key={5}>
                    過去5回以上
                  </option>
                  <option value={10} key={10}>
                    過去10回以上
                  </option>
                </select>
              </div>
            </div>
          </div>
          {isParticipationCountRangeErrorOn && (
            <div
              className="container col-md-8 col-lg-6 pt-2"
              style={{ fontSize: '12px' }}
            >
              <span style={{ color: '#ff5252' }}>下限が上限より大きいです</span>
            </div>
          )}

          <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2_5 mt-3 d-inline-block">
              このチケットの募集締切
              <span className="text-fz-small text-fw-normal">
                　※空欄の場合は無期限
              </span>
            </p>
          </div>
          <div className="shadow-sm mb-3 bg-white">
            <div className=" container col-md-8 col-lg-6 ">
              <label
                htmlFor="expired_at"
                className="row justify-content-between p-3_5"
              >
                <p className="pr-2 mb-0">締切日時</p>
                <input
                  type={datetimeFormType}
                  className="datetimepicker form-style-reset p-0 textarea--eventreport"
                  id="expired_at"
                  value={
                    eventsPlan.expired_at
                      ? moment(eventsPlan.expired_at).format(datetimeFormat)
                      : ''
                  }
                  onBlur={(e) => {
                    setEventsPlan({
                      ...eventsPlan,
                      expired_at: e.target.value,
                    });
                  }}
                  onChange={(e) => {
                    setEventsPlan({
                      ...eventsPlan,
                      expired_at: e.target.value,
                    });
                  }}
                  placeholder="2019/08/25 10:00"
                  autoComplete="off"
                />
              </label>
              <div
                className="text-fz-small text-black-50 text-right"
                style={{ marginTop: '-10px', paddingBottom: 10 }}
              >
                <span
                  onClick={() => {
                    setEventsPlan({ ...eventsPlan, expired_at: '' });
                  }}
                >
                  日時をクリアする
                </span>
              </div>
            </div>
          </div>
          <div className="container col-md-8 col-lg-6">
            <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-3 d-inline-block">
              このプランの募集ステータス
            </p>
          </div>
          <div className="shadow-sm mb-0 bg-white">
            <div className=" container col-md-8 col-lg-6 p-3_5">
              <select
                id="is_application_allowed"
                onBlur={() => updateEventsPlan()}
                onChange={(e) => {
                  setEventsPlan({
                    ...eventsPlan,
                    is_application_allowed: e.target.value === 'true',
                  });
                }}
                value={eventsPlan.is_application_allowed}
                className="p-0 w-100 textarea--eventreport custom-select custom-select--makecircle text-dark"
              >
                <option value={true}>募集中</option>
                <option value={false}>募集終了</option>
              </select>
            </div>
          </div>
          <div style={{ paddingBottom: '12px' }}>
            <div className="container col-md-8 col-lg-6">
              <button
                type="button"
                className="a-button a-button__top a-button--orange w-100 mb-5 mt-5"
                onClick={() => closeModal()}
              >
                OK
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SectionPlans;
